import React, { useEffect, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, panelGet, panelPost,
  useAdminGet,
} from '../kit';
import { Badge, Check, Labeled, inputCls } from '../email/emailKit';

/**
 * Admin → For You & What's New → "What's New · Settings"
 * (Backend/whatsnew.py, panel section "whatsnew"; app_settings `whatsnew_settings`).
 *
 *   Page          period of platform updates, card caps, sections on/off,
 *                 show removals, the page's texts
 *   Lists         news categories (label, colour, type labels) and the
 *                 industry list of the page's dropdown
 *   Snapshots     corpus snapshots (Take snapshot now) and the IP added /
 *                 removed changes detected between them (hide one from the page)
 */

const S = 'whatsnew';

interface Cat { key: string; label: string; color: string; types: string[] }
interface Form {
  period_days: string; max_new_universities: string; max_changes: string; per_category: string;
  world_default_days: string;
  sections: Record<string, boolean>; show_removed: boolean;
  texts: Record<string, string>;
  categories: (Cat & { typesText: string; isNew?: boolean })[];
  industriesText: string;
}

const toForm = (s: any): Form => ({
  period_days: String(s.period_days), max_new_universities: String(s.max_new_universities),
  max_changes: String(s.max_changes), per_category: String(s.per_category),
  world_default_days: String(s.world_default_days),
  sections: { ...s.sections }, show_removed: !!s.show_removed, texts: { ...s.texts },
  categories: (s.categories || []).map((c: Cat) => ({ ...c, typesText: c.types.join('\n') })),
  industriesText: (s.industries || []).join('\n'),
});

const NumField: React.FC<{ label: string; hint: string; value: string; onChange: (v: string) => void }> =
  ({ label, hint, value, onChange }) => (
  <Labeled label={label} hint={hint}>
    <input type="number" className={inputCls} value={value} onChange={e => onChange(e.target.value)} />
  </Labeled>
);

const SnapshotsSection: React.FC = () => {
  const snaps = useAdminGet<any>(S, 'snapshots');
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const changes = useAdminGet<any>(S, 'changes', { kind: kind === 'all' ? '' : kind, q }, [kind, q]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [ids, setIds] = useState<{ id: number; list: string[] } | null>(null);

  const take = async (force = false) => {
    setBusy(true); setNote(null);
    const { data, error } = await panelPost(S, 'snapshot_now', { force });
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setNote({ kind: data?.status === 'refused' ? 'error' : 'ok', text: `${data?.status}: ${data?.note || ''}` });
    snaps.reload(); changes.reload();
  };
  const hide = async (id: number, hidden: boolean) => {
    const { error } = await panelPost(S, 'change_hide', { id, hidden });
    if (error) setNote({ kind: 'error', text: error });
    changes.reload();
  };
  const showIds = async (id: number) => {
    const { data, error } = await panelGet(S, 'change_ids', { id });
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setIds({ id, list: data?.tech_ids || [] });
  };
  const latest = snaps.data?.latest;
  return (
    <Section title="Corpus snapshots & detected changes"
      right={<div className="flex gap-2">
        <Btn kind="primary" disabled={busy} onClick={() => take(false)}>{busy ? 'Working…' : 'Take snapshot now'}</Btn>
        <Btn small disabled={busy} onClick={() => take(true)}
          title="Store a snapshot even when the corpus has not changed">Force</Btn>
      </div>}>
      <p className="text-xs text-grayscale-1100 m-0">
        A snapshot records every university's technology ids. Each new one is compared with the previous one: the
        differences are the "IP added" / "IP removed" cards. Snapshots are taken by Admin → System → Search indexes
        (step "What's New snapshot"), at API start-up when the corpus changed, and with the button. The first one is
        the baseline: removals show from the next load on; additions before it come from the load dates.
      </p>
      {snaps.loading && !snaps.data ? <Loading /> : snaps.error ? <Msg kind="error">{snaps.error}</Msg> : (
        <KPIRow>
          <KPI label="Latest snapshot" value={latest ? `#${latest.id}` : 'none'} hint={latest ? fmtDate(latest.taken_at) : 'take the baseline'} />
          <KPI label="Technologies" value={latest ? latest.total.toLocaleString() : '—'} hint={latest ? `${latest.universities} universities` : ''} />
          <KPI label="Corpus since" value={snaps.data?.corpus_changed ? 'changed' : 'unchanged'}
            hint={snaps.data?.corpus_changed ? 'take a snapshot to record it' : 'nothing to record'} />
          <KPI label="Start-up check" value={snaps.data?.auto ? 'on' : 'off'} hint="WHATSNEW_SNAPSHOT_AUTO" />
        </KPIRow>
      )}
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      <DataTable rows={(snaps.data?.snapshots || []).map((r: any) => ({ ...r, baseline: r.is_baseline }))}
        columns={['id', 'taken_at', 'trigger', 'taken_by', 'total', 'universities', 'added', 'removed', 'unis_changed', 'baseline', 'seconds']}
        labels={{ id: '#', taken_at: 'Taken', unis_changed: 'Unis changed' }} csvName="whatsnew_snapshots"
        empty="No snapshot yet." maxHeight="18rem" />
      <div className="flex gap-2 items-center flex-wrap pt-2">
        <h4 className="text-xs font-bold text-grayscale-1300 m-0">Detected changes</h4>
        <Select value={kind} onChange={setKind} options={['all', 'added', 'removed']}
          labels={{ all: 'Added + removed', added: 'Added', removed: 'Removed' }} />
        <TextInput value={q} onChange={setQ} placeholder="University…" />
      </div>
      {changes.error && <Msg kind="error">{changes.error}</Msg>}
      <DataTable rows={(changes.data?.changes || []).map((r: any) => ({ ...r, tags: (r.tags || []).join(', ') }))}
        columns={['detected_at', 'university_name', 'country', 'kind', 'n', 'tags', 'is_new_university', 'hidden']}
        labels={{ detected_at: 'Detected', university_name: 'University', n: 'Listings', is_new_university: 'New uni' }}
        render={{ kind: r => <Badge tone={r.kind === 'added' ? 'green' : 'red'}>{r.kind}</Badge> }}
        actions={r => (
          <span className="flex gap-1.5">
            <Btn small onClick={() => showIds(r.id)}>Ids</Btn>
            <Btn small onClick={() => hide(r.id, !r.hidden)}>{r.hidden ? 'Show on page' : 'Hide from page'}</Btn>
          </span>
        )}
        csvName="whatsnew_changes" empty="No changes detected yet." maxHeight="22rem" />
      {ids && (
        <div className="rounded-lg border border-grayscale-300 p-3 text-xs flex flex-col gap-1.5">
          <div className="flex justify-between"><b>Change #{ids.id}: {ids.list.length} technology ids</b>
            <button type="button" className="underline" onClick={() => setIds(null)}>close</button></div>
          <div className="font-mono break-all max-h-40 overflow-auto">{ids.list.join(', ')}</div>
        </div>
      )}
    </Section>
  );
};

const WnSettings: React.FC = () => {
  const res = useAdminGet<any>(S, 'settings');
  const [f, setF] = useState<Form | null>(null);
  const [note, setNote] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (res.data) setF(toForm(res.data)); }, [res.data]);
  if (res.loading && !f) return <Loading />;
  if (res.error && !f) return <Msg kind="error">{res.error}</Msg>;
  if (!f) return null;
  const orphans = res.data?.orphans;

  const up = (patch: Partial<Form>) => setF(x => (x ? { ...x, ...patch } : x));
  const setCat = (i: number, patch: Partial<Form['categories'][number]>) =>
    up({ categories: f.categories.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const move = (i: number, d: number) => {
    const arr = f.categories.slice();
    const j = i + d;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    up({ categories: arr });
  };

  const save = async () => {
    setBusy(true); setNote(null);
    const body = {
      period_days: f.period_days, max_new_universities: f.max_new_universities, max_changes: f.max_changes,
      per_category: f.per_category, world_default_days: f.world_default_days,
      sections: f.sections, show_removed: f.show_removed, texts: f.texts,
      categories: f.categories.map(c => ({ key: c.isNew ? '' : c.key, label: c.label, color: c.color,
                                           types: c.typesText.split('\n') })),
    };
    const { data, error } = await panelPost(S, 'settings_set', body);
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setF(toForm(data));
    const o = data?.orphans || {};
    setNote({ kind: 'ok', text: 'Saved — the page shows it within five minutes.'
      + (o.category ? ` ${o.category} item(s) are in a category no longer listed and do not show.` : '')
      + (o.industry ? ` ${o.industry} item(s) carry an industry no longer listed.` : '') });
  };
  const reset = async () => {
    const { data, error } = await panelPost(S, 'settings_reset', {});
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setF(toForm(data)); setNote({ kind: 'ok', text: 'Back to the defaults.' });
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title="Page" right={<div className="flex gap-2">
        <DangerBtn onConfirm={reset}>Reset to defaults</DangerBtn>
        <Btn kind="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</Btn>
      </div>}>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <NumField label="Platform updates period" hint="days, 7–365" value={f.period_days} onChange={v => up({ period_days: v })} />
          <NumField label="New universities shown" hint="max cards" value={f.max_new_universities} onChange={v => up({ max_new_universities: v })} />
          <NumField label="Technology changes shown" hint="max cards" value={f.max_changes} onChange={v => up({ max_changes: v })} />
          <NumField label="News per category" hint="on the All tab" value={f.per_category} onChange={v => up({ per_category: v })} />
          <NumField label="News date range" hint="last N days, 0 = any" value={f.world_default_days} onChange={v => up({ world_default_days: v })} />
        </div>
        <div className="flex gap-5 flex-wrap">
          {([['platform', 'New on Cosmos section'], ['new_universities', 'New universities'],
             ['changes', 'Technology changes'], ['world', 'In the world section']] as [string, string][]).map(([k, l]) => (
            <Check key={k} checked={!!f.sections[k]} onChange={v => up({ sections: { ...f.sections, [k]: v } })}>{l}</Check>
          ))}
          <Check checked={f.show_removed} onChange={v => up({ show_removed: v })}>Show "IP removed" cards</Check>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* The crawl and news labels are no longer shown on the page (tracker, 2026-10-29). */}
          {([['title', 'Title'], ['subtitle', 'Subtitle']] as [string, string][]).map(([k, l]) => (
            <Labeled key={k} label={l}>
              <input className={inputCls} value={f.texts[k] || ''} onChange={e => up({ texts: { ...f.texts, [k]: e.target.value } })} />
            </Labeled>
          ))}
        </div>
        {note && <Msg kind={note.kind}>{note.text}</Msg>}
      </Section>

      <Section title="News categories & type labels"
        right={<Btn small onClick={() => up({ categories: [...f.categories,
          { key: '', label: '', color: '#80653F', types: [], typesText: '', isNew: true }] })}>Add category</Btn>}>
        <p className="text-xs text-grayscale-1100 m-0">
          The tabs of "In the world", in this order. Type labels: one per line (the editor offers these for the category).
          {orphans?.category ? ` ${orphans.category} item(s) sit in a category no longer listed.` : ''}
        </p>
        <div className="flex flex-col gap-2">
          {f.categories.map((c, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1.4fr_0.8fr_2fr_auto] gap-2 items-start rounded-lg border border-grayscale-300 p-2.5">
              <Labeled label={c.isNew ? 'Name (new)' : `Name · key ${c.key}`}>
                <input className={inputCls} value={c.label} onChange={e => setCat(i, { label: e.target.value })} />
              </Labeled>
              <Labeled label="Colour">
                <span className="flex items-center gap-2">
                  <input type="color" value={/^#[0-9a-f]{6}$/i.test(c.color) ? c.color : '#80653F'}
                    onChange={e => setCat(i, { color: e.target.value })} className="h-9 w-10 border-0 bg-transparent" />
                  <input className={inputCls} value={c.color} onChange={e => setCat(i, { color: e.target.value })} />
                </span>
              </Labeled>
              <Labeled label="Type labels" hint="one per line">
                <textarea className={`${inputCls} h-[4.5rem] py-1.5`} value={c.typesText}
                  onChange={e => setCat(i, { typesText: e.target.value })} />
              </Labeled>
              <div className="flex md:flex-col gap-1 pt-5">
                <Btn small onClick={() => move(i, -1)} disabled={i === 0}>↑</Btn>
                <Btn small onClick={() => move(i, 1)} disabled={i === f.categories.length - 1}>↓</Btn>
                <Btn small kind="danger" onClick={() => up({ categories: f.categories.filter((_, j) => j !== i) })}>Remove</Btn>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Industries (the page's dropdown and the item tag)">
        <p className="text-xs text-grayscale-1100">
          The same 14 industries as the Cosmos filter; fixed, so the two always match. Items tagged with an
          older industry show under its Cosmos counterpart.
          {orphans?.industry ? ` ${orphans.industry} item(s) carry an industry outside this list.` : ''}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(f.industriesText ? f.industriesText.split('\n') : []).map(x => (
            <span key={x} className="rounded-full border border-grayscale-300 px-2 py-0.5 text-xs">{x}</span>
          ))}
        </div>
      </Section>

      <SnapshotsSection />
    </div>
  );
};

export default WnSettings;
